/**
 * Projects.tsx — F33/F34/F35/F36: Projects & Quotes management
 * - F33: Create project (Admin)
 * - F34: Add quote to project (Admin)
 * - F35: Approve/Reject project or quote (Admin/Directivo)
 * - F36: View project status (all admin roles)
 *
 * Embeds as a tab in FinancialReports or can be routed standalone.
 */
import { useState, useEffect, useCallback } from 'react';
import {
  FolderPlus, FileText, CheckCircle2, XCircle, Clock,
  Plus, ArrowLeft, Building2, User, DollarSign, Calendar,
  AlertCircle, Loader2, Package,
} from 'lucide-react';
import { projectsAPI } from '../services/api';

// ─── Types ──────────────────────────────────────────────────────────────────
interface Quote {
  id: string;
  supplier_name: string;
  amount: number;
  description?: string;
  valid_until?: string;
  status?: 'pending' | 'approved' | 'rejected';
  created_at?: string;
}

interface Project {
  id: string;
  name: string;
  description: string;
  budget?: number;
  start_date?: string;
  end_date?: string;
  contractor?: string;
  status: 'draft' | 'pending' | 'ready_for_approval' | 'approved' | 'rejected';
  quotes?: Quote[];
  quoteCount?: number;
  created_at?: string;
}

interface Unit {
  id: string;
  label?: string;
  unit_number?: string;
}

// ─── Status Helpers ──────────────────────────────────────────────────────────
const statusConfig: Record<string, { label: string; color: string; icon: typeof Clock }> = {
  draft:           { label: 'Borrador',           color: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',  icon: FileText },
  pending:         { label: 'Pendiente',           color: 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400', icon: Clock },
  ready_for_approval: { label: 'Listo para aprobación', color: 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400', icon: AlertCircle },
  approved:        { label: 'Aprobado',            color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400', icon: CheckCircle2 },
  rejected:        { label: 'Rechazado',           color: 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400',   icon: XCircle },
};

function StatusBadge({ status }: { status: string }) {
  const cfg = statusConfig[status] || statusConfig.draft;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.color}`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0 }).format(amount || 0);
}

function formatDate(d?: string) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
}

// ─── Create Project Modal (F33) ─────────────────────────────────────────────
function CreateProjectModal({ condoId, onClose, onCreated }: {
  condoId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [form, setForm] = useState({
    name: '',
    description: '',
    budget: '',
    start_date: '',
    end_date: '',
    contractor: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) { setErrorMsg('El nombre es obligatorio'); return; }
    setSubmitting(true);
    setErrorMsg('');
    try {
      await projectsAPI.create(condoId, {
        name: form.name.trim(),
        description: form.description.trim(),
        budget: form.budget ? parseFloat(form.budget) : undefined,
        start_date: form.start_date || undefined,
        end_date: form.end_date || undefined,
        contractor: form.contractor.trim() || undefined,
      });
      onCreated();
      onClose();
    } catch (err: any) {
      setErrorMsg(err?.response?.data?.error || 'Error al crear el proyecto');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[var(--v-surface-1)] rounded-xl border border-slate-200 dark:border-[var(--v-surface-3)] p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">Nuevo Proyecto</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Nombre *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-[#36CCD8] outline-none"
              placeholder="Ej. Impermeabilización azotea"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Descripción</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              className="w-full px-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-[#36CCD8] outline-none"
              placeholder="Describe el proyecto..."
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Presupuesto</label>
              <input
                type="number"
                step="0.01"
                value={form.budget}
                onChange={(e) => setForm({ ...form, budget: e.target.value })}
                className="w-full px-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-[#36CCD8] outline-none"
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Contratista</label>
              <input
                type="text"
                value={form.contractor}
                onChange={(e) => setForm({ ...form, contractor: e.target.value })}
                className="w-full px-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-[#36CCD8] outline-none"
                placeholder="Nombre del contratista"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Fecha inicio</label>
              <input
                type="date"
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                className="w-full px-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-[#36CCD8] outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Fecha fin</label>
              <input
                type="date"
                value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                className="w-full px-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-[#36CCD8] outline-none"
              />
            </div>
          </div>
          {errorMsg && (
            <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {errorMsg}
            </div>
          )}
          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 bg-[#36CCD8] hover:bg-[#2bb9c5] disabled:opacity-50 text-white py-2.5 rounded-lg font-medium transition-colors"
            >
              {submitting ? 'Creando...' : 'Crear Proyecto'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 py-2.5 rounded-lg font-medium transition-colors"
            >
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Add Quote Modal (F34) ──────────────────────────────────────────────────
function AddQuoteModal({ condoId, project, onClose, onAdded }: {
  condoId: string;
  project: Project;
  onClose: () => void;
  onAdded: () => void;
}) {
  const [form, setForm] = useState({
    supplier_name: '',
    amount: '',
    description: '',
    valid_until: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.supplier_name.trim()) { setErrorMsg('El nombre del proveedor es obligatorio'); return; }
    if (!form.amount || parseFloat(form.amount) <= 0) { setErrorMsg('El monto debe ser mayor a 0'); return; }
    setSubmitting(true);
    setErrorMsg('');
    try {
      await projectsAPI.addQuote(condoId, project.id, {
        supplier_name: form.supplier_name.trim(),
        amount: parseFloat(form.amount),
        description: form.description.trim() || undefined,
        valid_until: form.valid_until || undefined,
      });
      onAdded();
      onClose();
    } catch (err: any) {
      setErrorMsg(err?.response?.data?.error || 'Error al agregar cotización');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[var(--v-surface-1)] rounded-xl border border-slate-200 dark:border-[var(--v-surface-3)] p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-1">Agregar Cotización</h3>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">{project.name}</p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Proveedor *</label>
            <input
              type="text"
              value={form.supplier_name}
              onChange={(e) => setForm({ ...form, supplier_name: e.target.value })}
              className="w-full px-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-[#36CCD8] outline-none"
              placeholder="Nombre de la empresa"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Monto *</label>
            <input
              type="number"
              step="0.01"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              className="w-full px-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-[#36CCD8] outline-none"
              placeholder="0.00"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Descripción</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2}
              className="w-full px-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-[#36CCD8] outline-none"
              placeholder="Detalle de la cotización..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Válido hasta</label>
            <input
              type="date"
              value={form.valid_until}
              onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
              className="w-full px-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-[#36CCD8] outline-none"
            />
          </div>
          {errorMsg && (
            <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {errorMsg}
            </div>
          )}
          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 bg-[#36CCD8] hover:bg-[#2bb9c5] disabled:opacity-50 text-white py-2.5 rounded-lg font-medium transition-colors"
            >
              {submitting ? 'Guardando...' : 'Agregar'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 py-2.5 rounded-lg font-medium transition-colors"
            >
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Confirm Dialog (F35) ────────────────────────────────────────────────────
function ConfirmDialog({ title, message, onConfirm, onCancel, confirmLabel, confirmColor }: {
  title: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  confirmLabel: string;
  confirmColor: 'green' | 'red';
}) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white dark:bg-[var(--v-surface-1)] rounded-xl border border-slate-200 dark:border-[var(--v-surface-3)] p-6 max-w-sm w-full">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">{title}</h3>
        <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">{message}</p>
        <div className="flex gap-3">
          <button
            onClick={onConfirm}
            className={`flex-1 py-2.5 rounded-lg font-medium text-white transition-colors ${
              confirmColor === 'green' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'
            }`}
          >
            {confirmLabel}
          </button>
          <button
            onClick={onCancel}
            className="flex-1 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 py-2.5 rounded-lg font-medium transition-colors"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Project Detail View ────────────────────────────────────────────────────
function ProjectDetail({ condoId, project, onBack, onChanged }: {
  condoId: string;
  project: Project;
  onBack: () => void;
  onChanged: () => void;
}) {
  const [detail, setDetail] = useState<Project>(project);
  const [loading, setLoading] = useState(false);
  const [showAddQuote, setShowAddQuote] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{ type: 'project' | 'quote'; id: string; action: 'approved' | 'rejected' } | null>(null);

  const loadDetail = useCallback(async () => {
    setLoading(true);
    try {
      const res = await projectsAPI.getById(condoId, project.id);
      const payload = res?.data?.data ?? res?.data ?? res;
      if (payload && typeof payload === 'object') setDetail(payload);
    } catch { /* keep existing detail */ }
    finally { setLoading(false); }
  }, [condoId, project.id]);

  useEffect(() => { loadDetail(); }, [loadDetail]);

  const quotes = detail.quotes || [];
  const quoteCount = detail.quoteCount ?? quotes.length;

  const handleStatusChange = async () => {
    if (!confirmAction) return;
    try {
      if (confirmAction.type === 'project') {
        await projectsAPI.updateStatus(condoId, project.id, confirmAction.action);
      } else {
        await projectsAPI.updateQuoteStatus(condoId, project.id, confirmAction.id, confirmAction.action);
      }
      await loadDetail();
      onChanged();
    } catch (err) {
      console.error('Error updating status:', err);
    } finally {
      setConfirmAction(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Back button */}
      <button
        onClick={onBack}
        className="inline-flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Volver a proyectos
      </button>

      {/* Project header */}
      <div className="bg-white dark:bg-[var(--v-surface-1)] rounded-xl border border-slate-200 dark:border-[var(--v-surface-3)] p-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Building2 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 dark:text-white truncate">{detail.name}</h3>
            </div>
            {detail.description && (
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-2">{detail.description}</p>
            )}
          </div>
          <div className="flex flex-col items-start sm:items-end gap-2 flex-shrink-0">
            <StatusBadge status={detail.status} />
            {detail.status !== 'approved' && detail.status !== 'rejected' && (
              <div className="flex gap-2">
                <button
                  onClick={() => setConfirmAction({ type: 'project', id: project.id, action: 'approved' })}
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm rounded-lg transition-colors"
                >
                  <CheckCircle2 className="w-4 h-4" /> Aprobar
                </button>
                <button
                  onClick={() => setConfirmAction({ type: 'project', id: project.id, action: 'rejected' })}
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg transition-colors"
                >
                  <XCircle className="w-4 h-4" /> Rechazar
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Project meta */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6 pt-6 border-t border-slate-100 dark:border-[var(--v-surface-3)]">
          {detail.budget != null && (
            <div>
              <p className="text-xs text-slate-500 dark:text-slate-400">Presupuesto</p>
              <p className="text-sm font-semibold text-slate-900 dark:text-white">{formatCurrency(detail.budget)}</p>
            </div>
          )}
          <div>
            <p className="text-xs text-slate-500 dark:text-slate-400">Fecha inicio</p>
            <p className="text-sm font-semibold text-slate-900 dark:text-white">{formatDate(detail.start_date)}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500 dark:text-slate-400">Fecha fin</p>
            <p className="text-sm font-semibold text-slate-900 dark:text-white">{formatDate(detail.end_date)}</p>
          </div>
          {detail.contractor && (
            <div>
              <p className="text-xs text-slate-500 dark:text-slate-400">Contratista</p>
              <p className="text-sm font-semibold text-slate-900 dark:text-white">{detail.contractor}</p>
            </div>
          )}
        </div>
      </div>

      {/* Quotes section */}
      <div className="bg-white dark:bg-[var(--v-surface-1)] rounded-xl border border-slate-200 dark:border-[var(--v-surface-3)] overflow-hidden">
        <div className="p-5 border-b border-slate-200 dark:border-[var(--v-surface-3)] flex items-center justify-between">
          <div>
            <h4 className="font-semibold text-slate-900 dark:text-white">Cotizaciones</h4>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {quoteCount} cotización{quoteCount !== 1 ? 'es' : ''}
              {quoteCount < 3 && <span className="text-amber-600 dark:text-amber-400 ml-1">· Se requieren mínimo 3</span>}
            </p>
          </div>
          <button
            onClick={() => setShowAddQuote(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#36CCD8] hover:bg-[#2bb9c5] text-white text-sm rounded-lg font-medium transition-colors"
          >
            <Plus className="w-4 h-4" /> Agregar
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : quotes.length === 0 ? (
          <div className="p-12 text-center">
            <Package className="w-10 h-10 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
            <p className="text-slate-500 dark:text-slate-400 text-sm">Sin cotizaciones registradas</p>
            <p className="text-slate-400 dark:text-slate-500 text-xs mt-1">Agrega cotizaciones de proveedores para este proyecto</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100 dark:divide-slate-700">
            {quotes.map((q) => {
              const qStatus = q.status || 'pending';
              return (
                <div key={q.id} className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-slate-900 dark:text-white">{q.supplier_name}</span>
                      <StatusBadge status={qStatus === 'pending' ? 'pending' : qStatus} />
                    </div>
                    <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-slate-500 dark:text-slate-400">
                      <span className="font-semibold text-slate-700 dark:text-slate-300 text-sm">{formatCurrency(q.amount)}</span>
                      {q.valid_until && (
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="w-3 h-3" /> Válido hasta {formatDate(q.valid_until)}
                        </span>
                      )}
                    </div>
                    {q.description && (
                      <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{q.description}</p>
                    )}
                  </div>
                  {qStatus !== 'approved' && qStatus !== 'rejected' && (
                    <div className="flex gap-2 flex-shrink-0">
                      <button
                        onClick={() => setConfirmAction({ type: 'quote', id: q.id, action: 'approved' })}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs rounded-lg transition-colors"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" /> Aprobar
                      </button>
                      <button
                        onClick={() => setConfirmAction({ type: 'quote', id: q.id, action: 'rejected' })}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs rounded-lg transition-colors"
                      >
                        <XCircle className="w-3.5 h-3.5" /> Rechazar
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modals */}
      {showAddQuote && (
        <AddQuoteModal
          condoId={condoId}
          project={detail}
          onClose={() => setShowAddQuote(false)}
          onAdded={() => { loadDetail(); onChanged(); }}
        />
      )}
      {confirmAction && (
        <ConfirmDialog
          title={confirmAction.action === 'approved' ? 'Confirmar aprobación' : 'Confirmar rechazo'}
          message={
            confirmAction.type === 'project'
              ? `¿Estás seguro de ${confirmAction.action === 'approved' ? 'aprobar' : 'rechazar'} el proyecto "${detail.name}"?`
              : `¿Estás seguro de ${confirmAction.action === 'approved' ? 'aprobar' : 'rechazar'} esta cotización?`
          }
          confirmLabel={confirmAction.action === 'approved' ? 'Aprobar' : 'Rechazar'}
          confirmColor={confirmAction.action === 'approved' ? 'green' : 'red'}
          onConfirm={handleStatusChange}
          onCancel={() => setConfirmAction(null)}
        />
      )}
    </div>
  );
}

// ─── Main Projects Component ────────────────────────────────────────────────
export default function Projects({ condoId }: { condoId: string }) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  const loadProjects = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await projectsAPI.getAll(condoId);
      const payload = res?.data?.data ?? res?.data ?? res;
      const list = payload?.projects ?? payload?.items ?? (Array.isArray(payload) ? payload : []);
      setProjects(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Error loading projects:', err);
      setError(true);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, [condoId]);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  // ─── Guard: no condo ─────────────────────────────────────────────────────
  if (!condoId) return (
    <div className="flex items-center justify-center py-12">
      <div className="text-center">
        <div className="w-14 h-14 bg-slate-100 dark:bg-slate-700 rounded-2xl flex items-center justify-center mx-auto mb-3">
          <Building2 className="w-7 h-7 text-slate-400 dark:text-slate-500" />
        </div>
        <h3 className="text-base font-semibold text-slate-900 dark:text-white mb-1">Sin condominio asignado</h3>
        <p className="text-sm text-slate-500 dark:text-slate-400">No se ha asignado un condominio a tu cuenta.</p>
      </div>
    </div>
  );

  // ─── Project detail view ─────────────────────────────────────────────────
  if (selectedProject) {
    return (
      <ProjectDetail
        condoId={condoId}
        project={selectedProject}
        onBack={() => setSelectedProject(null)}
        onChanged={loadProjects}
      />
    );
  }

  // ─── List view ───────────────────────────────────────────────────────────
  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h3 className="text-lg font-bold text-slate-900 dark:text-white">Proyectos</h3>
          <p className="text-sm text-slate-500 dark:text-slate-400">Gestión de proyectos y cotizaciones</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#36CCD8] hover:bg-[#2bb9c5] text-white text-sm rounded-lg font-medium transition-colors"
        >
          <FolderPlus className="w-4 h-4" /> Nuevo Proyecto
        </button>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <div className="bg-white dark:bg-[var(--v-surface-1)] rounded-xl border border-slate-200 dark:border-[var(--v-surface-3)] p-8 text-center">
          <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
          <p className="text-slate-600 dark:text-slate-400 text-sm">No se pudieron cargar los proyectos.</p>
          <button onClick={loadProjects} className="mt-3 text-sm text-[#36CCD8] hover:underline">Reintentar</button>
        </div>
      )}

      {/* Empty */}
      {!loading && !error && projects.length === 0 && (
        <div className="bg-white dark:bg-[var(--v-surface-1)] rounded-xl border border-slate-200 dark:border-[var(--v-surface-3)] p-12 text-center">
          <div className="w-14 h-14 bg-slate-100 dark:bg-slate-700 rounded-2xl flex items-center justify-center mx-auto mb-3">
            <Package className="w-7 h-7 text-slate-400 dark:text-slate-500" />
          </div>
          <h4 className="text-base font-semibold text-slate-900 dark:text-white mb-1">Sin proyectos registrados</h4>
          <p className="text-sm text-slate-500 dark:text-slate-400">Crea un proyecto para solicitar cotizaciones de proveedores.</p>
        </div>
      )}

      {/* Project cards */}
      {!loading && !error && projects.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {projects.map((p) => {
            const qc = p.quoteCount ?? p.quotes?.length ?? 0;
            return (
              <button
                key={p.id}
                onClick={() => setSelectedProject(p)}
                className="text-left bg-white dark:bg-[var(--v-surface-1)] rounded-xl border border-slate-200 dark:border-[var(--v-surface-3)] p-5 hover:shadow-md hover:border-[#36CCD8]/40 transition-all"
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-9 h-9 bg-blue-100 dark:bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Building2 className="w-4.5 h-4.5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <h4 className="font-semibold text-slate-900 dark:text-white truncate">{p.name}</h4>
                  </div>
                  <StatusBadge status={p.status} />
                </div>
                {p.description && (
                  <p className="text-sm text-slate-500 dark:text-slate-400 line-clamp-2 mb-3">{p.description}</p>
                )}
                <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500 dark:text-slate-400">
                  {p.budget != null && (
                    <span className="inline-flex items-center gap-1">
                      <DollarSign className="w-3 h-3" /> {formatCurrency(p.budget)}
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1">
                    <FileText className="w-3 h-3" /> {qc} cotización{qc !== 1 ? 'es' : ''}
                  </span>
                  {p.contractor && (
                    <span className="inline-flex items-center gap-1">
                      <User className="w-3 h-3" /> {p.contractor}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <CreateProjectModal
          condoId={condoId}
          onClose={() => setShowCreate(false)}
          onCreated={loadProjects}
        />
      )}
    </div>
  );
}
